import {
  applyAppConfig,
  OctSettings,
  RequiredImageTypes,
} from '@/imageFiles';

// NOTE: this module must NOT statically import '@/api'. api.ts imports this
// module for getUrlParam/patient_id, so a static import here creates a cycle:
// index.tsx imports appConfig first, whose top-level import of api would run
// api's module body before appConfig finished initializing searchParams —
// throwing "Cannot access 'searchParams' before initialization". loadAppConfig
// imports api lazily instead, after this module (and its URL params) is ready.

// Guarded access to browser globals so this module can be imported in
// non-browser contexts (SSR, tests) without crashing at import time.
const searchParams: URLSearchParams = typeof window === 'undefined'
  ? new URLSearchParams()
  : new URL(window.location.href).searchParams;

export function getUrlParam(name: string): string | null {
  return searchParams.get(name);
}

export const patient_id = parseInt(
  getUrlParam("selectedPatientId")
  || process.env.REACT_APP_VIVID_PT_ID
  || "0"
);

export const crc_name = getUrlParam("crc_name")
  || process.env.REACT_APP_VIVID_CRC_NAME
  || ""
;

export const session_uuid =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });


// Project-level configuration fetched from the server on app init. Mirrors the
// shape of public/appConfig.json. Values previously hardcoded in imageFiles.ts
// (requiredImageTypes, octSettings) now originate here.
export interface AppConfig {
  projectId: string;
  requireLayoutBothEyes: boolean;
  requireImagesBothEyes: boolean;
  chooseBlindspotArrays: boolean;
  requiredImageTypes: RequiredImageTypes;
  octSettings: OctSettings;
}

let current: AppConfig | null = null;

// Throws if called before loadAppConfig() has resolved. Safe to use anywhere
// in the app graph, which is only mounted after load.
export function getAppConfig(): AppConfig {
  if (!current)
    throw new Error("app config has not been loaded yet");
  return current;
}

function validate(d: any): asserts d is AppConfig {
  const missing: string[] = [];
  if (typeof d !== 'object' || d === null)
    throw new Error("appConfig.json did not parse to an object");
  if (typeof d.requireLayoutBothEyes !== 'boolean') missing.push('requireLayoutBothEyes');
  if (typeof d.requireImagesBothEyes !== 'boolean') missing.push('requireImagesBothEyes');
  if (typeof d.chooseBlindspotArrays !== 'boolean') missing.push('chooseBlindspotArrays');
  if (typeof d.requiredImageTypes !== 'object' || d.requiredImageTypes === null)
    missing.push('requiredImageTypes');
  const oct = d.octSettings;
  if (typeof oct !== 'object' || oct === null
      || typeof oct.enfaceAngle !== 'number'
      || typeof oct.totalBscans !== 'number'
      || typeof oct.patternSize !== 'object' || oct.patternSize === null
      || typeof oct.patternSize.w !== 'number'
      || typeof oct.patternSize.h !== 'number')
    missing.push('octSettings');
  if (missing.length)
    throw new Error(`appConfig.json is missing or has invalid fields: ${missing.join(', ')}`);
}

// Fetches app config, validates, and applies the
// OCT/image values to imageFiles.ts. Rejects on network error or invalid config;
// callers treat that as fatal (see src/index.tsx).
export async function loadAppConfig(): Promise<AppConfig> {
  let res: Response;
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_VIVID_CFG) {
    res = await fetch('/appDefaults.json', { cache: 'no-store' });
  } else {
    // Lazy import breaks the appConfig <-> api cycle (see note at top of file).
    // By now this module is fully initialized, so authPatient's URL params
    // (u, p, patient_id) are available for the server to key the config on.
    const { api, authPatient } = await import('@/api');
    res = await fetch(api.getProtocol, {
      method: 'POST',
      body: JSON.stringify(authPatient),
    });
  }
  if (!res.ok)
    throw new Error(`failed to fetch app configuration (${res.status} ${res.statusText})`);
  let data = await res.json();
  if (data.hasOwnProperty('data'))
    data = data.data.ruleset.stimulus_placer;
  validate(data);
  current = data;
  applyAppConfig({
    requiredImageTypes: data.requiredImageTypes,
    octSettings: data.octSettings,
  });
  return data;
}
